// ============================================================================
// privateDatasets — datasets sensíveis (financeiro/comercial) que ANTES viviam
// em public/data/*.json (vazavam SEM login no site publicado — auditoria
// 10/09/2026). Agora vivem no Firestore em
//   artifacts/portalrda-f312d/public/data/private_datasets/{id}
// cobertos pela regra K do firestore.rules (allow read, write: if isAdmin()).
// Só o usuário master (email @rodrigodemeterco.adv.br / robo@rda.adv.br) lê.
//
// Cada doc: { payload: "<json stringificado>", bytes, updatedAt }.
// Guardar como string (não objeto aninhado) evita os limites de campo/
// profundidade do Firestore — só o limite de 1 MiB por doc, e os 3 cabem.
//
// Escrita: só pelos scripts geradores via Admin SDK
// (scripts/python_tools/recta_scripta/gera_*.py + o gerador do diário).
// ============================================================================
import { doc, getDoc } from 'firebase/firestore';
import { db } from './firebase/config';

const BASE = 'artifacts/portalrda-f312d/public/data/private_datasets';

// ids válidos
export const DATASET = {
  BLING_SNAPSHOT: 'recta_bling_snapshot',
  SCRIPTA_INDEX: 'recta_scripta_index',
  FINANCE_MOVIMENTACOES: 'finance_movimentacoes',
};

const _cache = new Map();
const _inflight = new Map();

/**
 * Lê e parseia um dataset privado do Firestore. Cache em memória por sessão.
 * @param {string} datasetId  um valor de DATASET
 * @param {{force?: boolean}} [opts]
 * @returns {Promise<any>} objeto já parseado
 * @throws Error com .code = 'DATASET_NOT_FOUND' | 'DATASET_INVALID' | (erro Firestore/permissão)
 */
export async function loadPrivateDataset(datasetId, { force = false } = {}) {
  if (!force && _cache.has(datasetId)) return _cache.get(datasetId);
  if (!force && _inflight.has(datasetId)) return _inflight.get(datasetId);

  const promise = (async () => {
    const snap = await getDoc(doc(db, `${BASE}/${datasetId}`));
    if (!snap.exists()) {
      const e = new Error('DATASET_NOT_FOUND');
      e.code = 'DATASET_NOT_FOUND';
      e.datasetId = datasetId;
      throw e;
    }
    const payload = snap.data()?.payload;
    if (typeof payload !== 'string') {
      const e = new Error('DATASET_INVALID');
      e.code = 'DATASET_INVALID';
      e.datasetId = datasetId;
      throw e;
    }
    const parsed = JSON.parse(payload);
    _cache.set(datasetId, parsed);
    return parsed;
  })();

  _inflight.set(datasetId, promise);
  try {
    return await promise;
  } finally {
    _inflight.delete(datasetId);
  }
}

export function clearPrivateDatasetCache(datasetId) {
  if (datasetId) _cache.delete(datasetId);
  else _cache.clear();
}
